import { ArrowForward } from "@mui/icons-material";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
} from "@mui/lab";
import { Box, Chip, Typography } from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { HistoryData } from "../../../types/inventoryData";
import { useInventory } from "../InventoryProvider";
import { useTranslation } from "react-i18next";

type timelineTypes = {
  history: HistoryData[] | undefined;
};

const ProductTimeline: React.FC<timelineTypes> = ({ history }) => {
  const { selectedProduct } = useInventory();
  const { t } = useTranslation();

  const changedHistory = () => {
    if (!history) return [];

    const result: {
      changedAt: string;
      items: HistoryData[];
    }[] = [];
    let lastTimestamp: string | null = null;

    history.forEach((h) => {
      const formattedDate = format(
        new Date(h.changedAt),
        "yyyy-MM-dd HH:mm:ss"
      );

      const lastGroup = result[result.length - 1];

      if (lastGroup && lastGroup.changedAt == formattedDate) {
        lastGroup.items.push(h);
      } else {
        result.push({ changedAt: formattedDate, items: [h] });
      }
    });
    return result;
  };

  const groupedHistory = changedHistory();

  return (
    <Timeline
      sx={{
        [`& .${timelineItemClasses.root}:before`]: {
          flex: 0,
          padding: 0,
        },
      }}
    >
      {groupedHistory.map((group, index) => (
        <TimelineItem key={group.changedAt}>
          <TimelineSeparator>
            <TimelineDot />
            <TimelineConnector />
          </TimelineSeparator>

          <TimelineContent
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              marginBottom: 2,
              maxWidth: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography variant="subtitle2">
                {group.items[0].changedBy}
              </Typography>
              <Typography variant="caption" color="GrayText">
                {group.changedAt}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {group.items.map((item) => {
                return (
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                    key={item.historyId}
                  >
                    <Typography variant="body2">{item.fieldName}</Typography>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Chip
                        label={item.oldValue}
                        variant="outlined"
                        size="medium"
                        sx={{
                          height: "auto",
                          "& .MuiChip-label": {
                            display: "block",
                            whiteSpace: "normal",
                          },
                        }}
                      />
                      <ArrowForward fontSize="small" color="disabled" />
                      <Chip
                        label={item.newValue}
                        variant="outlined"
                        size="medium"
                        sx={{
                          height: "auto",
                          "& .MuiChip-label": {
                            display: "block",
                            whiteSpace: "normal",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </TimelineContent>
        </TimelineItem>
      ))}
      <TimelineItem>
        <TimelineSeparator>
          <TimelineDot />
        </TimelineSeparator>

        <TimelineContent
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            marginBottom: 2,
            maxWidth: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography variant="subtitle2">
              {t("inventory.productAdded")}
            </Typography>
            <Typography variant="caption" color="GrayText">
              {selectedProduct?.createdAt &&
                format(
                  new Date(selectedProduct.createdAt),
                  "yyyy-MM-dd HH:mm:ss"
                )}
            </Typography>
          </Box>
        </TimelineContent>
      </TimelineItem>
    </Timeline>
  );
};

export default ProductTimeline;
